import type { ReactNode } from 'react'
import { Badge } from '@/components/ui/badge'
import type { SearchCandidatesResponse } from '@/hooks/use-briefs'
import type { OpportunityMatch, OpportunitySearchResponse, UnifiedCandidate } from '@/hooks/use-smart-search'

export const SMART_ANALYSIS_UI_DOCS = `Shared Analysis UI for **candidate** (\`direction=opportunities\`) and **employer** (\`direction=candidates\`) flows.

**Component stack (do not duplicate in wrappers):**
| Piece | File | Role |
| --- | --- | --- |
| Copy | \`labs/src/lib/smart-analysis-copy.ts\` | Operational intro strings + textarea placeholders per direction (same for idle and retry) |
| Instructions | \`SmartAnalysisInstructionsField\` | Shared "Additional preferences" field |
| Run CTA | \`SmartAnalysisRunButton\` | Toolbar + intro primary action |
| Intro | \`SmartAnalysisIntroCard\` | Story + instructions + in-card run (idle/retry only) |
| Run panel | \`SmartAnalysisRunPanel\` | Single card with Analysis header, progress, and summary |
| List count | \`PaneListCount\` | Results count line above card grids |
| Tiers | \`SmartAnalysisTierDigest\` / \`SmartAnalysisTierSection\` | Verdict strip + Strong matches / Good matches / Not recommended sections (candidates direction; \`labs/src/lib/smart-analysis-tiers.ts\`). Public labels via \`labs/src/lib/ranking-vocabulary.ts\`. |
| Employer leaderboard | \`SmartAnalysisOutcomeSummary\` / \`SmartAnalysisLeaderboard\` | Employer Analysis only (\`presentation="leaderboard"\`): summary card, segmented bar, top-3 rank icons |
| Pagination | \`SmartAnalysisResultsPagination\` | Crop ranked lists (default 10 total: top 3 + 7 remainder); show more (+10) and view all; precluded tier paginated separately |
| Shell | \`SmartAnalysisResultsShell\` | State machine: intro, run panel, populated, filter-empty |

**Wrappers** (\`CandidateSmartSearchPanel\`, \`EmployerSmartSearchPanel\`) only wire domain logic. Candidate: run API, apply filter, browse bridge. Employer job workspace: run API against active applicants only (\`introJobScoped\`), no Save to brief and no invite on results (invite via Suggestions). Pass \`introInstructions\`, \`onIntroInstructionsChange\`, optional \`introPreferencesLeadingSlot\`, and \`toolbarStart={<SmartAnalysisRunButton />}\`.

**Visibility:** Intro renders when there are **no ranked matches** (never run or zero-match retry). Zero-match retry shows a **collapsed one-line last-run summary** above the intro (not the run panel). Populated runs use **\`SmartAnalysisRunPanel\`** (summary + progress); result counts live in **\`PaneListCount\`** only. Hidden intro when populated or searching; \`toolbarStart\` run button shows only when intro is hidden.

**Storybook:** Catalog → Search → Analysis UI (overview); colocated stories on each component + results shell.`

export const mockCandidateResults: SearchCandidatesResponse = {
  session_id: 'session-candidates-1',
  total_candidates_evaluated: 4,
  llm_judged_count: 4,
  search_summary: 'Strong matches on platform engineering and TypeScript depth.',
  strategies_used: ['skill', 'semantic'],
  top_matches: [
    {
      rank: 1,
      profile_id: 'profile-1',
      candidate_name: 'Sofia Rinaldi',
      final_score: 0.85,
      structured_score: 0.9,
      semantic_score: 0.8,
      was_llm_judged: true,
      llm_reasoning: 'Excellent fit for the role.',
      analysis_headline: 'Strong platform engineering depth with TypeScript leadership.',
      found_by: ['skill'],
      category: 'top',
    },
    {
      rank: 2,
      profile_id: 'profile-2',
      candidate_name: 'Marcus Chen',
      final_score: 0.58,
      structured_score: 0.6,
      semantic_score: 0.55,
      was_llm_judged: true,
      llm_reasoning: 'Solid engineering background, lighter on platform work.',
      analysis_headline: 'Solid backend skills; lighter on platform-specific experience.',
      found_by: ['semantic'],
      category: 'compelling',
    },
    {
      rank: 3,
      profile_id: 'profile-3',
      candidate_name: 'Priya Nair',
      final_score: 0.52,
      structured_score: 0.5,
      semantic_score: 0.55,
      was_llm_judged: true,
      llm_reasoning: 'Adjacent experience; worth a conversation.',
      analysis_headline: 'Adjacent product experience; worth a screening conversation.',
      found_by: ['semantic'],
      category: 'compelling',
    },
    {
      rank: 4,
      profile_id: 'profile-4',
      candidate_name: 'Tomás Alvarez',
      final_score: 0.3,
      structured_score: 0.35,
      semantic_score: 0.25,
      was_llm_judged: true,
      llm_reasoning: 'Limited overlap with the required stack.',
      found_by: ['skill'],
      category: 'precluded',
    },
  ],
}

const MOCK_NAMES = [
  'Alex Kim',
  'Jordan Lee',
  'Sam Patel',
  'Riley Okafor',
  'Casey Morgan',
  'Drew Santos',
  'Jamie Wu',
  'Morgan Blake',
  'Taylor Reed',
  'Quinn Harper',
  'Avery Cole',
  'Blake Rivera',
  'Cameron Ellis',
  'Dakota Flynn',
  'Emery Shaw',
]

function mockRankedCandidate(
  rank: number,
  options?: { category?: 'top' | 'compelling' | 'precluded'; score?: number },
): SearchCandidatesResponse['top_matches'][number] {
  const score = options?.score ?? Math.max(0.25, 0.9 - rank * 0.04)
  const category =
    options?.category ??
    (score >= 0.65 ? 'top' : score >= 0.45 ? 'compelling' : 'precluded')
  const name = MOCK_NAMES[(rank - 4) % MOCK_NAMES.length] ?? `Candidate ${rank}`
  return {
    rank,
    profile_id: `profile-large-${rank}`,
    candidate_name: name,
    final_score: score,
    structured_score: Math.min(1, score + 0.05),
    semantic_score: Math.max(0, score - 0.05),
    was_llm_judged: true,
    llm_reasoning: `Evaluation summary for ${name}.`,
    analysis_headline: `Rank ${rank} — ${category} tier match for this role.`,
    found_by: ['semantic'],
    category,
  }
}

/** 18 ranked candidates for pagination / view-all stories. */
export const mockLargeCandidateResults: SearchCandidatesResponse = {
  session_id: 'session-candidates-large',
  total_candidates_evaluated: 18,
  llm_judged_count: 18,
  search_summary:
    'Several strong matches in the active applicant pool; most candidates align on core stack with varying depth.',
  strategies_used: ['skill', 'semantic'],
  top_matches: [
    ...mockCandidateResults.top_matches,
    ...Array.from({ length: 14 }, (_, index) => mockRankedCandidate(index + 5)),
  ],
}

export const mockOpportunityResults: OpportunitySearchResponse = {
  session_id: 'session-opportunities-1',
  total_candidates_evaluated: 1,
  llm_judged_count: 1,
  search_summary: 'One strong role match based on your sustainability profile.',
  strategies_used: ['semantic'],
  top_matches: [
    {
      rank: 1,
      opportunity_id: 'opp-1',
      role_title: 'AI Product Lead',
      company_name: 'Example Co',
      final_score: 0.82,
      recommendation: 'Strong alignment with product and AI experience.',
      found_by: ['semantic'],
    },
  ],
}

export const mockEmptySessionCandidates: SearchCandidatesResponse = {
  session_id: 'session-empty',
  total_candidates_evaluated: 0,
  llm_judged_count: 0,
  top_matches: [],
}

export const mockEmptySessionOpportunities: OpportunitySearchResponse = {
  session_id: 'session-empty',
  total_candidates_evaluated: 0,
  llm_judged_count: 0,
  top_matches: [],
}

export const mockLiveCandidates: UnifiedCandidate[] = []
export const mockLiveOpportunities: OpportunityMatch[] = []

export const mockCompletedAnalysisStages = [
  { stage: 'complete', content: 'Search complete', timestamp: Date.now(), strategy: 'skill' },
]

export const mockStrategyProgress = {
  skill: { candidatesFound: 47, totalProcessed: 1, isComplete: true },
  semantic: { candidatesFound: 20, totalProcessed: 20, isComplete: true },
}

export const mockBrowsePreferencesLeadingSlot: ReactNode = (
  <div className="space-y-2">
    <p className="text-sm font-medium">Preferences from browse</p>
    <div className="flex flex-wrap gap-2">
      <Badge variant="secondary">Remote</Badge>
      <Badge variant="secondary">Sustainability</Badge>
    </div>
  </div>
)

export const mockJobReportApplicants = [
  {
    id: 'app-1',
    profile: {
      id: 'profile-1',
      name: 'Sofia Rinaldi',
      headline: 'Staff engineer',
      avatar_initials: 'SR',
      avatar_url: null,
      top_skills: ['TypeScript', 'Platform'],
    },
    opportunity_id: 'job-1',
    status: 'in_review',
    source: 'direct',
    current_stage: { slug: 'in_review', name: 'In review', order: 10, is_terminal: false },
    days_in_stage: 2,
    applied_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date().toISOString(),
    has_thread: false,
    event_count: 0,
    match_score: 0.88,
    evaluation_id: 'eval-1',
    board_position: 1000,
  },
  {
    id: 'app-2',
    profile: {
      id: 'profile-invited',
      name: 'Marcus Chen',
      headline: 'Backend lead',
      avatar_initials: 'MC',
      avatar_url: null,
      top_skills: ['Python', 'AWS'],
    },
    opportunity_id: 'job-1',
    status: 'invited',
    source: 'referral',
    current_stage: null,
    days_in_stage: null,
    applied_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date().toISOString(),
    has_thread: false,
    event_count: 0,
    match_score: 0.84,
    evaluation_id: 'eval-2',
    board_position: 2000,
  },
  {
    id: 'app-3',
    profile: {
      id: 'profile-3',
      name: 'Priya Nair',
      headline: 'Product engineer',
      avatar_initials: 'PN',
      avatar_url: null,
      top_skills: ['React'],
    },
    opportunity_id: 'job-1',
    status: 'applied',
    source: 'direct',
    current_stage: { slug: 'applied', name: 'Applied', order: 0, is_terminal: false },
    days_in_stage: 1,
    applied_at: new Date(Date.now() - 30 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date().toISOString(),
    has_thread: false,
    event_count: 0,
    match_score: null,
    evaluation_id: null,
    board_position: 3000,
  },
]

/** Pipeline applicants with no linked evaluation scores — job report empty state. */
export const mockJobReportApplicantsUnanalysed = mockJobReportApplicants.map((applicant) => ({
  ...applicant,
  match_score: null,
  evaluation_id: null,
}))

const MOCK_LEADERBOARD_NAMES = [
  'Diego Morales',
  'Rachel Okoro',
  'Yasmin Farouk',
  'Samir Al-Hassan',
  'Elena Vasquez',
  'Noah Bergstrom',
  'Mina Okafor',
]

/** Seven strong-band ranked candidates for job-report compact leaderboard rows (#4+). */
export const mockJobReportLeaderboardMatches: SearchCandidatesResponse['top_matches'] =
  MOCK_LEADERBOARD_NAMES.map((name, index) => ({
    rank: index + 1,
    profile_id: `profile-leaderboard-${index + 1}`,
    candidate_name: name,
    final_score: 0.85 - index * 0.01,
    structured_score: 0.88,
    semantic_score: 0.82,
    was_llm_judged: true,
    llm_reasoning: `Strong alignment for ${name}.`,
    analysis_headline: `Strong match on core role requirements.`,
    found_by: ['semantic'],
    category: 'top' as const,
  }))

export const mockJobReportLeaderboardCandidates = mockJobReportLeaderboardMatches.map((match) => ({
  profile_id: match.profile_id,
  name: match.candidate_name,
  score: match.final_score,
  state: 'analyzed' as const,
  rank: match.rank,
  full: match,
}))

export const mockJobReportLeaderboardApplicants = mockJobReportLeaderboardMatches.map(
  (match, index) => ({
    id: `app-leaderboard-${index + 1}`,
    profile: {
      id: match.profile_id,
      name: match.candidate_name,
      headline: 'Engineer',
      avatar_initials: match.candidate_name
        .split(' ')
        .map((part) => part[0])
        .join('')
        .slice(0, 2),
      avatar_url: null,
      top_skills: ['TypeScript'],
    },
    opportunity_id: 'job-1',
    status: index === 6 ? 'invited' : 'applied',
    source: 'direct',
    current_stage:
      index === 6
        ? null
        : { slug: 'applied', name: 'Applied', order: 0, is_terminal: false },
    days_in_stage: index === 6 ? null : 1,
    applied_at: new Date(Date.now() - (index + 1) * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date().toISOString(),
    has_thread: false,
    event_count: 0,
    match_score: match.final_score,
    evaluation_id: `eval-leaderboard-${index + 1}`,
    board_position: (index + 1) * 1000,
    nudged_at: null,
  }),
)

export const mockJobAnalysisCoverage = {
  total_candidates: 3,
  requirements: [
    { label: 'TypeScript', covered: 1, total: 3 },
    { label: 'Platform engineering', covered: 1, total: 3 },
    { label: 'Leadership', covered: 0, total: 3 },
  ],
}

export const mockJobProspectMatches = [
  {
    rank: 1,
    profile_id: 'prospect-1',
    name: 'Alex Kim',
    headline: 'Full-stack engineer',
    top_skills: ['TypeScript'],
    semantic_score: 0.8,
    match_percent: 82,
    avatar_url: null,
  },
  {
    rank: 2,
    profile_id: 'prospect-2',
    name: 'Jordan Lee',
    headline: 'Platform engineer',
    top_skills: ['Go'],
    semantic_score: 0.78,
    match_percent: 81,
    avatar_url: null,
  },
]
